import { DialogComponent } from '@theme/dialog';
import { debounce } from '@theme/utilities';

const PRODUCT_SEARCH_TYPE = 'product';
const PREDICTIVE_SECTION_ID = 'predictive-search';
const SEARCH_SECTION_ID = 'search';
const SEARCH_OPTIONS_PREFIX = 'last';
const MIN_QUERY_LENGTH = 1;

/**
 * @typedef {Object} SearchDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} trigger
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} clearButton
 * @property {HTMLElement} scrollArea
 * @property {HTMLElement} defaultState
 * @property {HTMLElement} predictiveState
 * @property {HTMLElement} results
 * @property {HTMLAnchorElement} viewAll
 * @property {HTMLElement} status
 */

/** @extends {DialogComponent} */
class SearchDialog extends DialogComponent {
  /** @type {SearchDialogRefs} */
  refs = /** @type {SearchDialogRefs} */ ({});

  requiredRefs = ['dialog', 'trigger', 'input', 'clearButton', 'scrollArea', 'defaultState', 'predictiveState', 'results', 'viewAll', 'status'];

  #abortController = new AbortController();

  #requestToken = 0;

  /** @type {Map<string, { predictiveMarkup: string, exactCount: number | null }>} */
  #queryCache = new Map();

  #handleInput = debounce(() => {
    this.#updateResults();
  }, 250);

  #handleInputEvent = () => {
    this.#syncClearButtonVisibility();
    this.#handleInput();
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;

    this.refs.input.addEventListener('input', this.#handleInputEvent, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('dialog:open', this.#handleDialogOpen, { signal });
    this.addEventListener('dialog:close', this.#handleDialogClose, { signal });
    this.#syncClearButtonVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#handleInput.cancel();
    this.#abortController.abort();
  }

  #handleDialogOpen = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.#syncClearButtonVisibility();

    requestAnimationFrame(() => {
      this.refs.input.focus({ preventScroll: true });

      if (this.refs.input.value) {
        this.refs.input.select();
        this.#updateResults();
      } else {
        this.#showDefaultState();
      }
    });
  };

  #handleDialogClose = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#reset();
    this.refs.trigger.focus({ preventScroll: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const fillButton = event.target.closest('[data-search-fill]');
    if (fillButton instanceof HTMLElement) {
      const value = fillButton.getAttribute('data-search-fill')?.trim();
      if (!value) return;

      event.preventDefault();
      this.refs.input.value = value;
      this.#syncClearButtonVisibility();
      this.refs.input.focus({ preventScroll: true });
      this.#updateResults();
      return;
    }

    const clearButton = event.target.closest('[ref="clearButton"]');
    if (!(clearButton instanceof HTMLElement)) return;

    event.preventDefault();
    this.#handleInput.cancel();
    this.#reset();
    this.refs.input.focus({ preventScroll: true });
  };

  async #updateResults() {
    const query = this.refs.input.value.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      this.#showDefaultState();
      return;
    }

    const cacheEntry = this.#queryCache.get(query);
    if (cacheEntry) {
      this.#renderPredictiveResults(query, cacheEntry);
      return;
    }

    this.#showLoadingState();

    const requestToken = ++this.#requestToken;

    try {
      const [predictiveMarkup, exactCount] = await Promise.all([
        this.#fetchPredictiveMarkup(query),
        this.#fetchExactCount(query),
      ]);

      if (requestToken !== this.#requestToken) return;

      const result = { predictiveMarkup, exactCount };
      this.#queryCache.set(query, result);
      this.#renderPredictiveResults(query, result);
    } catch {
      if (requestToken !== this.#requestToken) return;
      this.#showErrorState();
    }
  }

  #showDefaultState() {
    this.#requestToken += 1;
    this.refs.defaultState.hidden = false;
    this.refs.predictiveState.hidden = true;
    this.refs.viewAll.hidden = true;
    this.refs.results.innerHTML = '';
    this.#setResultsBusy(false);
    this.refs.status.textContent = '';
    this.refs.scrollArea.scrollTop = 0;
  }

  /**
   */
  #showLoadingState() {
    this.refs.defaultState.hidden = true;
    this.refs.predictiveState.hidden = false;
    this.refs.viewAll.hidden = true;
    this.#setResultsBusy(true);
    this.refs.results.innerHTML = '<div class="header-search__loading" aria-hidden="true"><span class="header-search__loader"></span></div>';
    this.refs.status.textContent = this.loadingText;
    this.refs.scrollArea.scrollTop = 0;
  }

  #showErrorState() {
    this.refs.defaultState.hidden = true;
    this.refs.predictiveState.hidden = false;
    this.refs.viewAll.hidden = true;
    this.#setResultsBusy(false);
    this.refs.results.innerHTML = `<p class="header-search__feedback body-2">${this.errorText}</p>`;
    this.refs.status.textContent = this.errorText;
    this.refs.scrollArea.scrollTop = 0;
  }

  /**
   * @param {string} query
   * @param {{ predictiveMarkup: string, exactCount: number | null }} result
   */
  #renderPredictiveResults(query, result) {
    this.refs.defaultState.hidden = true;
    this.refs.predictiveState.hidden = false;
    this.refs.results.innerHTML = result.predictiveMarkup;
    this.#setResultsBusy(false);
    this.#highlightProductMatches(query);

    const exactCount = typeof result.exactCount === 'number' ? result.exactCount : 0;
    const hasResults = exactCount > 0;

    this.refs.viewAll.href = this.#buildSearchPageUrl(query).toString();
    this.refs.viewAll.textContent = this.#formatTemplate(this.viewProductsTemplate, { count: `${exactCount}` });
    this.refs.viewAll.hidden = !hasResults;
    this.refs.status.textContent = hasResults
      ? this.#formatTemplate(this.resultsTemplate, { count: `${exactCount}`, terms: query })
      : this.#formatTemplate(this.noResultsTemplate, { terms: query });
    this.refs.scrollArea.scrollTop = 0;
  }

  /** @param {string} query */
  async #fetchPredictiveMarkup(query) {
    const url = this.#buildPredictiveUrl(query);
    const responseText = await this.#fetchHTML(url);
    const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
    const section = documentFragment.querySelector(`#shopify-section-${PREDICTIVE_SECTION_ID}`);

    if (!(section instanceof HTMLElement)) {
      throw new Error('Predictive search section was not returned.');
    }

    return section.innerHTML;
  }

  /** @param {string} query */
  async #fetchExactCount(query) {
    const url = this.#buildSearchSectionUrl(query);
    const responseText = await this.#fetchHTML(url);
    const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
    const marker = documentFragment.querySelector('[data-search-results-count]');

    if (!(marker instanceof HTMLElement)) {
      return null;
    }

    const value = Number(marker.dataset.searchResultsCount);
    return Number.isFinite(value) ? value : null;
  }

  /**
   * @param {URL} url
   */
  async #fetchHTML(url) {
    const response = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    const text = await response.text();

    if (!response.ok) {
      throw new Error(text || `${response.status}`);
    }

    return text;
  }

  /**
   * @param {string} query
   */
  #buildPredictiveUrl(query) {
    const url = new URL(this.predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');
    url.searchParams.set('section_id', PREDICTIVE_SECTION_ID);
    url.searchParams.sort();
    return url;
  }

  /**
   * @param {string} query
   */
  #buildSearchSectionUrl(query) {
    const url = this.#buildSearchPageUrl(query);
    url.searchParams.set('section_id', SEARCH_SECTION_ID);
    url.searchParams.sort();
    return url;
  }

  /**
   * @param {string} query
   */
  #buildSearchPageUrl(query) {
    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', PRODUCT_SEARCH_TYPE);
    url.searchParams.set('options[prefix]', SEARCH_OPTIONS_PREFIX);
    return url;
  }

  /**
   * @param {string} query
   */
  #highlightProductMatches(query) {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    for (const element of this.refs.results.querySelectorAll('[data-search-highlight-source]')) {
      if (!(element instanceof HTMLElement)) continue;

      const source = element.dataset.searchHighlightSource ?? '';
      if (!source) continue;

      element.innerHTML = highlightText(source, normalizedQuery);
    }
  }

  #reset() {
    this.refs.input.value = '';
    this.#syncClearButtonVisibility();
    this.#showDefaultState();
  }

  #syncClearButtonVisibility() {
    this.refs.clearButton.hidden = this.refs.input.value.length === 0;
  }

  /**
   * @param {boolean} isBusy
   */
  #setResultsBusy(isBusy) {
    this.refs.results.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  /**
   * @param {string} template
   * @param {{ count?: string, terms?: string }} replacements
   */
  #formatTemplate(template, replacements) {
    return template
      .replace('__COUNT__', replacements.count ?? '')
      .replace('__TERMS__', replacements.terms ?? '');
  }

  get searchUrl() {
    return this.dataset.searchUrl ?? '/search';
  }

  get predictiveSearchUrl() {
    return this.dataset.predictiveSearchUrl ?? '/search/suggest';
  }

  get loadingText() {
    return this.dataset.loadingText ?? 'Loading...';
  }

  get errorText() {
    return this.dataset.errorText ?? 'Search is currently unavailable.';
  }

  get resultsTemplate() {
    return this.dataset.resultsTemplate ?? '__COUNT__ results found for __TERMS__';
  }

  get noResultsTemplate() {
    return this.dataset.noResultsTemplate ?? 'No results found for __TERMS__';
  }

  get viewProductsTemplate() {
    return this.dataset.viewProductsTemplate ?? 'View __COUNT__ products';
  }
}

/**
 * @param {string} value
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @param {string} source
 * @param {string} query
 */
function highlightText(source, query) {
  const pattern = new RegExp(escapeRegExp(query), 'ig');
  const matches = [...source.matchAll(pattern)];

  if (!matches.length) {
    return escapeHTML(source);
  }

  let cursor = 0;
  let result = '';

  for (const match of matches) {
    const index = match.index ?? 0;
    const matchedValue = match[0] ?? '';

    result += escapeHTML(source.slice(cursor, index));
    result += `<strong>${escapeHTML(matchedValue)}</strong>`;
    cursor = index + matchedValue.length;
  }

  result += escapeHTML(source.slice(cursor));

  return result;
}

/**
 * @param {string} value
 */
function escapeHTML(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('search-dialog')) {
  customElements.define('search-dialog', SearchDialog);
}